// frontend/src/Admin.js
// Admin page — manage scholarship records without SQL.
// Protected by API secret (POC auth). Replace with proper auth before public launch.

import { useState, useEffect, useCallback } from "react";
import { API_URL, adminHeaders } from "./config";
import AdminMetrics from './AdminMetrics';

const PLATFORMS = [
  "commonapp","fastweb","scholarships_com","bigfuture",
  "coalition_app","unigo","niche","sallie_mae","appily",
  "scholarshipowl","bold_org","going_merry","collegexpress","other"
];


// ── Helpers ────────────────────────────────────────────────────────────────

function Badge({ active }) {
  return (
    <span style={{
      fontSize: 11, padding: "2px 8px", borderRadius: 99, fontWeight: 600,
      background: active ? "#d5f5e3" : "#fadbd8",
      color: active ? "#1a6b3a" : "#922b21",
    }}>{active ? "Active" : "Inactive"}</span>
  );
}

function Btn({ children, onClick, variant = "primary", disabled, style }) {
  const base = {
    fontSize: 12, padding: "7px 14px", borderRadius: 6, fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer", border: "none",
    opacity: disabled ? 0.6 : 1, fontFamily: "sans-serif", ...style
  };
  const variants = {
    primary:   { background: "#1f4e79", color: "#fff" },
    danger:    { background: "#e74c3c", color: "#fff" },
    success:   { background: "#1d9e75", color: "#fff" },
    secondary: { background: "#f0f4f8", color: "#2c3e50", border: "1px solid #d0d8e4" },
  };
  return <button style={{ ...base, ...variants[variant] }} onClick={onClick} disabled={disabled}>{children}</button>;
}

// ── Scholarship Form (Add / Edit) ──────────────────────────────────────────

const EMPTY_FORM = {
  name: "", provider_name: "", source_platform: "", source_id: "",
  amount_min: "", amount_max: "", amount_is_renewable: false,
  deadline: "", application_url: "", description: "",
  eligible_regions: "", eligible_states: "", eligible_majors: "",
  eligible_degrees: "", gpa_minimum: "", sat_minimum: "", act_minimum: "",
  first_gen_required: false, financial_need_based: false,
  merit_based: false, essay_required: false, letters_required: false,
};

function ScholarshipForm({ initial, onSave, onCancel, allScholarships }) {
  const [form, setForm] = useState(() => initial ? {
    ...EMPTY_FORM,
    ...initial,
    eligible_regions:  (initial.eligible_regions  || []).join(", "),
    eligible_states:   (initial.eligible_states   || []).join(", "),
    eligible_majors:   (initial.eligible_majors   || []).join(", "),
    eligible_degrees:  (initial.eligible_degrees  || []).join(", "),
    deadline: initial.deadline ? initial.deadline.slice(0, 10) : "",
  } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");
  const [mergeTarget, setMergeTarget] = useState("");
  const [merging, setMerging] = useState(false);

  function set(field, value) { setForm(f => ({ ...f, [field]: value })); }

  function parseArr(str) { return str.split(",").map(s => s.trim()).filter(Boolean); }

  async function handleSave() {
    if (!form.name.trim()) { setError("Name is required."); return; }
    setSaving(true); setError("");
    const payload = {
      ...form,
      amount_min:       form.amount_min       ? Number(form.amount_min)  : null,
      amount_max:       form.amount_max       ? Number(form.amount_max)  : null,
      gpa_minimum:      form.gpa_minimum      ? Number(form.gpa_minimum) : null,
      sat_minimum:      form.sat_minimum      ? Number(form.sat_minimum) : null,
      act_minimum:      form.act_minimum      ? Number(form.act_minimum) : null,
      eligible_regions: parseArr(form.eligible_regions),
      eligible_states:  parseArr(form.eligible_states),
      eligible_majors:  parseArr(form.eligible_majors),
      eligible_degrees: parseArr(form.eligible_degrees),
    };
    try {
      const url    = initial ? `${API_URL}/api/scholarships/${initial.id}` : `${API_URL}/api/scholarships`;
      const method = initial ? "PATCH" : "POST";
      const res    = await fetch(url, { method, headers: adminHeaders(), body: JSON.stringify(payload) });
      const json   = await res.json();
      if (!res.ok) { setError(json.error || "Save failed."); return; }
      onSave();
    } catch { setError("Network error."); }
    finally { setSaving(false); }
  }

  async function handleMerge() {
    if (!mergeTarget) return;
    setMerging(true); setError("");
    try {
      const res  = await fetch(`${API_URL}/api/scholarships/${initial.id}/merge`, {
        method: "PATCH", headers: adminHeaders(),
        body: JSON.stringify({ canonical_id: mergeTarget })
      });
      const json = await res.json();
      if (!res.ok) { setError(json.error || "Merge failed."); return; }
      onSave();
    } catch { setError("Network error."); }
    finally { setMerging(false); }
  }

  const F = ({ label, field, type = "text", placeholder }) => (
    <div style={{ marginBottom: 14 }}>
      <label style={ls.label}>{label}</label>
      <input
        type={type} value={form[field]} placeholder={placeholder}
        onChange={e => set(field, e.target.value)}
        style={ls.input}
      />
    </div>
  );

  const Check = ({ label, field }) => (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#2c3e50", marginBottom: 10, cursor: "pointer", fontFamily: "sans-serif" }}>
      <input type="checkbox" checked={form[field]} onChange={e => set(field, e.target.checked)} />
      {label}
    </label>
  );

  return (
    <div style={ls.modal}>
      <div style={ls.modalBox}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={ls.modalTitle}>{initial ? "Edit Scholarship" : "Add Scholarship"}</h2>
          <button onClick={onCancel} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#8a95a3" }}>✕</button>
        </div>

        {error && <p style={ls.error}>{error}</p>}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
          <div style={{ gridColumn: "1/-1" }}><F label="Scholarship Name *" field="name" placeholder="e.g. Niche $2,000 No Essay Scholarship" /></div>
          <F label="Provider Name" field="provider_name" placeholder="e.g. Niche" />
          <div style={{ marginBottom: 14 }}>
            <label style={ls.label}>Platform</label>
            <select value={form.source_platform} onChange={e => set("source_platform", e.target.value)} style={ls.input}>
              <option value="">— Select —</option>
              {PLATFORMS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <F label="Amount Min ($)" field="amount_min" type="number" />
          <F label="Amount Max ($)" field="amount_max" type="number" />
          <F label="Deadline" field="deadline" type="date" />
          <div style={{ gridColumn: "1/-1" }}><F label="Application URL" field="application_url" placeholder="https://..." /></div>
          <div style={{ gridColumn: "1/-1" }}><F label="Description" field="description" placeholder="Brief description" /></div>
          <F label="GPA Minimum" field="gpa_minimum" type="number" placeholder="e.g. 3.0" />
          <F label="SAT Minimum" field="sat_minimum" type="number" placeholder="e.g. 1200" />
          <F label="ACT Minimum" field="act_minimum" type="number" placeholder="e.g. 25" />
          <div style={{ gridColumn: "1/-1" }}><F label="Eligible Majors (comma-separated)" field="eligible_majors" placeholder="e.g. Engineering, Business" /></div>
          <div style={{ gridColumn: "1/-1" }}><F label="Eligible States (comma-separated)" field="eligible_states" placeholder="e.g. California, Texas" /></div>
          <div style={{ gridColumn: "1/-1" }}><F label="Eligible Regions (comma-separated)" field="eligible_regions" placeholder="e.g. Northeast, Southeast" /></div>
          <div style={{ gridColumn: "1/-1" }}><F label="Eligible Degrees (comma-separated)" field="eligible_degrees" placeholder="e.g. Bachelor's, Master's" /></div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "0 40px", margin: "8px 0 20px" }}>
          <Check label="Essay Required"        field="essay_required" />
          <Check label="Letters Required"      field="letters_required" />
          <Check label="Merit Based"           field="merit_based" />
          <Check label="Financial Need Based"  field="financial_need_based" />
          <Check label="First Gen Required"    field="first_gen_required" />
          <Check label="Renewable"             field="amount_is_renewable" />
        </div>

        {/* Merge section — only for existing records */}
        {initial && (
          <div style={{ borderTop: "1px solid #e8edf2", paddingTop: 16, marginBottom: 20 }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#8a95a3", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, fontFamily: "sans-serif" }}>Merge into canonical record</p>
            <p style={{ fontSize: 12, color: "#6b7a8d", marginBottom: 10, fontFamily: "sans-serif" }}>This marks the current record as a duplicate and deactivates it.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <select value={mergeTarget} onChange={e => setMergeTarget(e.target.value)} style={{ ...ls.input, flex: 1 }}>
                <option value="">— Select canonical record —</option>
                {allScholarships.filter(s => s.id !== initial.id).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <Btn variant="danger" onClick={handleMerge} disabled={!mergeTarget || merging}>
                {merging ? "Merging…" : "Merge"}
              </Btn>
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
          <Btn variant="success" onClick={handleSave} disabled={saving}>{saving ? "Saving…" : initial ? "Save Changes" : "Add Scholarship"}</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Partnerships Tab ──────────────────────────────────────────────────────

function PartnershipsTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [form, setForm] = useState({
    buyer_name: '', buyer_email: '', license_count: 10,
    price_per_license: 47, label: '', code_expiry_days: 90, mode: 'gift'
  });
  const [error, setError] = useState('');

  const headers = adminHeaders();

  async function loadOrders() {
    try {
      const res = await fetch(`${API_URL}/api/partnerships/orders`, { headers });
      const data = await res.json();
      setOrders(data.orders || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function loadOrderDetail(id) {
    try {
      const res = await fetch(`${API_URL}/api/partnerships/orders/${id}`, { headers });
      const data = await res.json();
      setSelectedOrder(data);
    } catch (err) { console.error(err); }
  }

  async function createOrder(e) {
    e.preventDefault();
    setError('');
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/partnerships/orders`, {
        method: 'POST', headers,
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (res.ok) {
        setShowCreate(false);
        setForm({ buyer_name: '', buyer_email: '', license_count: 10, price_per_license: 47, label: '', code_expiry_days: 90, mode: 'gift' });
        loadOrders();
        loadOrderDetail(data.order.id);
      } else {
        setError(data.error || 'Failed to create order.');
      }
    } catch (err) { setError('Something went wrong.'); }
    setCreating(false);
  }

  async function sendInvoice(orderId) {
    setSendingInvoice(orderId);
    try {
      const res = await fetch(`${API_URL}/api/partnerships/orders/${orderId}/send-invoice`, {
        method: 'POST', headers
      });
      if (res.ok) { loadOrders(); if (selectedOrder?.order?.id === orderId) loadOrderDetail(orderId); }
    } catch (err) { console.error(err); }
    setSendingInvoice(null);
  }

  useEffect(() => { loadOrders(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const statusColor = { draft: '#718096', invoiced: '#D69E2E', paid: '#48BB78', expired: '#E53E3E' };
  const codeStatusColor = { pending: '#A0AEC0', active: '#48BB78', redeemed: '#3182CE', expired: '#E53E3E' };

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: '#718096' }}>Loading...</div>;

  // ── Order Detail View ──
  if (selectedOrder) {
    const o = selectedOrder.order;
    const codes = selectedOrder.codes || [];
    const redeemed = codes.filter(c => c.status === 'redeemed').length;
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px', fontFamily: 'sans-serif' }}>
        <button onClick={() => setSelectedOrder(null)} style={{ background: 'none', border: 'none', color: '#1f4e79', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>← Back to Orders</button>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h3 style={{ margin: '0 0 4px', fontSize: 20, color: '#2D3748' }}>{o.buyer_name}</h3>
              <p style={{ margin: 0, color: '#718096', fontSize: 14 }}>{o.buyer_email}</p>
              {o.label && <p style={{ margin: '8px 0 0', color: '#4A5568', fontSize: 14, fontStyle: 'italic' }}>{o.label}</p>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ background: statusColor[o.status] || '#718096', color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{o.status.toUpperCase()}</span>
              <p style={{ margin: '8px 0 0', fontSize: 22, fontWeight: 700, color: '#2D3748' }}>${parseFloat(o.total_amount).toFixed(2)}</p>
              <p style={{ margin: 0, color: '#718096', fontSize: 13 }}>{o.license_count} × ${parseFloat(o.price_per_license).toFixed(2)}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 24, marginTop: 16, fontSize: 13, color: '#718096' }}>
            <span>Mode: {o.mode}</span>
            <span>Created: {new Date(o.created_at).toLocaleDateString()}</span>
            {o.paid_at && <span>Paid: {new Date(o.paid_at).toLocaleDateString()}</span>}
            <span>Code expiry: {o.code_expiry_days} days</span>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            {o.status === 'draft' && (
              <button onClick={() => sendInvoice(o.id)} disabled={sendingInvoice === o.id}
                style={{ background: '#1f4e79', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {sendingInvoice === o.id ? 'Sending...' : '📧 Send Invoice'}
              </button>
            )}
            <button onClick={() => {
              const activeCodes = codes.filter(c => c.status === 'active' || c.status === 'pending').map(c => c.code).join('\n');
              navigator.clipboard.writeText(activeCodes);
            }} style={{ background: '#EDF2F7', color: '#2D3748', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              📋 Copy Codes
            </button>
          </div>
        </div>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24 }}>
          <h4 style={{ margin: '0 0 16px', fontSize: 16, color: '#2D3748' }}>Redemption Codes ({redeemed} / {codes.length} redeemed)</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#718096', fontWeight: 600 }}>Code</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#718096', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#718096', fontWeight: 600 }}>Redeemed By</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#718096', fontWeight: 600 }}>Redeemed</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', color: '#718096', fontWeight: 600 }}>Expires</th>
              </tr>
            </thead>
            <tbody>
              {codes.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontWeight: 600, letterSpacing: 1 }}>{c.code}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: codeStatusColor[c.status] || '#A0AEC0', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{c.status}</span>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#4A5568' }}>
                    {c.redeemed_by_name ? `${c.redeemed_by_name} (${c.redeemed_by_email})` : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#718096' }}>
                    {c.redeemed_at ? new Date(c.redeemed_at).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#718096' }}>
                    {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── Create Order Form ──
  if (showCreate) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px 16px', fontFamily: 'sans-serif' }}>
        <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', color: '#1f4e79', cursor: 'pointer', fontSize: 14, fontWeight: 600, marginBottom: 16 }}>← Back to Orders</button>

        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 32 }}>
          <h3 style={{ margin: '0 0 24px', fontSize: 20, color: '#2D3748' }}>Create Partnership Order</h3>
          <form onSubmit={createOrder}>
            <label style={formLabel}>Buyer Name *</label>
            <input value={form.buyer_name} onChange={e => setForm({ ...form, buyer_name: e.target.value })} required style={formInput} placeholder="e.g. John Smith" />

            <label style={formLabel}>Buyer Email *</label>
            <input type="email" value={form.buyer_email} onChange={e => setForm({ ...form, buyer_email: e.target.value })} required style={formInput} placeholder="buyer@example.com" />

            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={formLabel}>Number of Licenses *</label>
                <input type="number" value={form.license_count} onChange={e => setForm({ ...form, license_count: parseInt(e.target.value) || 1 })} min={1} max={500} required style={formInput} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={formLabel}>Price per License ($)</label>
                <input type="number" value={form.price_per_license} onChange={e => setForm({ ...form, price_per_license: parseFloat(e.target.value) || 47 })} min={0} step={0.01} style={formInput} />
              </div>
            </div>

            <div style={{ background: '#EDF2F7', padding: '12px 16px', borderRadius: 8, margin: '12px 0 16px', fontSize: 15, fontWeight: 600, color: '#2D3748' }}>
              Total: ${((form.license_count || 0) * (form.price_per_license || 0)).toFixed(2)}
            </div>

            <label style={formLabel}>Label (optional)</label>
            <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} style={formInput} placeholder="e.g. Sigma Chi Alumni Gift 2026" />

            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={formLabel}>Mode</label>
                <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })} style={formInput}>
                  <option value="gift">Gift / Donation (no dashboard)</option>
                  <option value="managed" disabled>Managed Partnership (Phase 2)</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={formLabel}>Code Expiry (days)</label>
                <input type="number" value={form.code_expiry_days} onChange={e => setForm({ ...form, code_expiry_days: parseInt(e.target.value) || 90 })} min={7} max={365} style={formInput} />
              </div>
            </div>

            {error && <p style={{ color: '#E53E3E', fontSize: 14, marginTop: 12 }}>{error}</p>}

            <button type="submit" disabled={creating} style={{ width: '100%', marginTop: 24, padding: '12px 24px', background: '#1f4e79', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
              {creating ? 'Creating Order...' : 'Create Order & Generate Codes'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Orders List View ──
  const totalRevenue = orders.filter(o => o.status === 'paid').reduce((s, o) => s + parseFloat(o.total_amount), 0);
  const totalRedeemed = orders.reduce((s, o) => s + parseInt(o.redeemed_count || 0), 0);
  const totalCodes = orders.reduce((s, o) => s + parseInt(o.total_codes || 0), 0);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px', fontFamily: 'sans-serif' }}>
      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Orders', value: orders.length, color: '#1f4e79' },
          { label: 'Revenue', value: `$${totalRevenue.toFixed(2)}`, color: '#48BB78' },
          { label: 'Codes Redeemed', value: `${totalRedeemed} / ${totalCodes}`, color: '#3182CE' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 24px', flex: '1 1 150px' }}>
            <div style={{ fontSize: 12, color: '#718096', fontWeight: 600, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Create button */}
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => setShowCreate(true)} style={{ background: '#1f4e79', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          + New Partnership Order
        </button>
      </div>

      {/* Orders table */}
      {orders.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 48, textAlign: 'center', color: '#718096' }}>
          No partnership orders yet. Click "New Partnership Order" to create one.
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0', background: '#F7FAFC' }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Buyer</th>
                <th style={thStyle}>Label</th>
                <th style={thStyle}>Licenses</th>
                <th style={thStyle}>Redeemed</th>
                <th style={thStyle}>Total</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id} style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }} onClick={() => loadOrderDetail(o.id)}>
                  <td style={tdStyle}>{new Date(o.created_at).toLocaleDateString()}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, color: '#2D3748' }}>{o.buyer_name}</div>
                    <div style={{ fontSize: 11, color: '#A0AEC0' }}>{o.buyer_email}</div>
                  </td>
                  <td style={{ ...tdStyle, color: '#718096', fontStyle: 'italic' }}>{o.label || '—'}</td>
                  <td style={tdStyle}>{o.license_count}</td>
                  <td style={tdStyle}>{o.redeemed_count || 0} / {o.total_codes || o.license_count}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>${parseFloat(o.total_amount).toFixed(2)}</td>
                  <td style={tdStyle}>
                    <span style={{ background: statusColor[o.status] || '#718096', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{o.status.toUpperCase()}</span>
                  </td>
                  <td style={tdStyle}>
                    {o.status === 'draft' && (
                      <button onClick={(e) => { e.stopPropagation(); sendInvoice(o.id); }} disabled={sendingInvoice === o.id}
                        style={{ background: '#1f4e79', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                        {sendingInvoice === o.id ? '...' : 'Send Invoice'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const formLabel = { display: 'block', fontSize: 13, fontWeight: 600, color: '#4A5568', marginBottom: 4, marginTop: 16 };
const formInput = { width: '100%', padding: '10px 12px', fontSize: 14, border: '1px solid #E2E8F0', borderRadius: 6, outline: 'none', boxSizing: 'border-box' };
const thStyle = { padding: '10px 12px', textAlign: 'left', color: '#718096', fontWeight: 600, fontSize: 12 };
const tdStyle = { padding: '10px 12px' };


// ── Students Tab ───────────────────────────────────────────────────────────

function StudentsTab() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [search, setSearch]     = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${API_URL}/api/admin/students`, { headers: adminHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setStudents(json.students || []);
    } catch (e) { setError(e.message || "Failed to load students."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = students.filter(s =>
    s.full_name.toLowerCase().includes(search.toLowerCase()) ||
    s.email.toLowerCase().includes(search.toLowerCase())
  );

  function fmt(dateStr) {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }

  function profileStatus(s) {
    if (!s.profile_version) return { label: "No Profile", color: "#922b21", bg: "#fadbd8" };
    if (Number(s.matched) > 0) return { label: "Matched", color: "#1a6b3a", bg: "#d5f5e3" };
    return { label: "Profile Only", color: "#7d5a00", bg: "#fef9e7" };
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 16px" }}>

      {/* Summary cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 20 }}>
        {[
          { label: "Total Students", value: students.length, color: "#1f4e79" },
          { label: "Profiles Created", value: students.filter(s => s.profile_version).length, color: "#1d9e75" },
          { label: "Matches Run", value: students.filter(s => Number(s.matched) > 0).length, color: "#7c3aed" },
          { label: "Applications Tracked", value: students.reduce((sum, s) => sum + Number(s.tracked_applications || 0), 0), color: "#b45309" },
        ].map(card => (
          <div key={card.label} style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: "16px 20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#8a95a3", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{card.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: card.color }}>{card.value}</div>
          </div>
        ))}
      </div>

      {error && <div style={ls.errorBanner}>{error}</div>}

      {/* Search + refresh */}
      <div style={{ background: "#fff", borderRadius: 10, padding: "14px 18px", marginBottom: 16, display: "flex", gap: 12, alignItems: "center", border: "1px solid #e2e8f0" }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or email…"
          style={{ ...ls.input, flex: 1, marginBottom: 0 }}
        />
        <Btn variant="secondary" onClick={load}>↻ Refresh</Btn>
        <span style={{ fontSize: 12, color: "#8a95a3" }}>{filtered.length} students</span>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f8faff", borderBottom: "2px solid #e2e8f0" }}>
              {["Name", "Email", "Joined", "Profile", "GPA (W)", "Matched", "Excluded", "Tracked", "Status"].map(h => (
                <th key={h} style={ls.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ textAlign: "center", padding: "40px", color: "#8a95a3" }}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ textAlign: "center", padding: "40px", color: "#8a95a3" }}>No students found.</td></tr>
            ) : filtered.map(s => {
              const status = profileStatus(s);
              return (
                <tr key={s.id} style={{ borderBottom: "1px solid #f0f4f8" }}>
                  <td style={ls.td}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#1c2833" }}>{s.full_name}</div>
                    <div style={{ fontSize: 11, color: "#8a95a3", marginTop: 2 }}>{s.grade_level || "—"}</div>
                  </td>
                  <td style={ls.td}><span style={{ fontSize: 12, color: "#6b7a8d" }}>{s.email}</span></td>
                  <td style={ls.td}><span style={{ fontSize: 12, color: "#6b7a8d" }}>{fmt(s.created_at)}</span></td>
                  <td style={ls.td}>
                    {s.profile_version
                      ? <span style={{ fontSize: 12, color: "#6b7a8d" }}>v{s.profile_version} · {fmt(s.profile_updated_at)}</span>
                      : <span style={{ fontSize: 12, color: "#e74c3c" }}>None</span>
                    }
                  </td>
                  <td style={{ ...ls.td, textAlign: "center" }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1c2833" }}>
                      {s.gpa_weighted ? Number(s.gpa_weighted).toFixed(2) : "—"}
                    </span>
                  </td>
                  <td style={{ ...ls.td, textAlign: "center" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1d9e75" }}>{s.matched || 0}</span>
                  </td>
                  <td style={{ ...ls.td, textAlign: "center" }}>
                    <span style={{ fontSize: 13, color: "#8a95a3" }}>{s.excluded || 0}</span>
                  </td>
                  <td style={{ ...ls.td, textAlign: "center" }}>
                    <span style={{ fontSize: 13, color: "#7c3aed", fontWeight: 600 }}>{s.tracked_applications || 0}</span>
                  </td>
                  <td style={ls.td}>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, fontWeight: 600, background: status.bg, color: status.color }}>
                      {status.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Admin component ───────────────────────────────────────────────────

export default function Admin() {
  const [tab, setTab] = useState("scholarships"); // "scholarships" | "students" | "metrics"
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [search, setSearch]             = useState("");
  const [activeFilter, setActiveFilter] = useState("true");
  const [editing, setEditing]           = useState(null);
  const [togglingId, setTogglingId]     = useState(null);
  const [validating, setValidating]     = useState(false);
  const [validateResult, setValidateResult] = useState(null);

  async function validateUrls() {
    setValidating(true); setValidateResult(null);
    try {
      const res  = await fetch(`${API_URL}/api/admin/validate-urls`, { method: "POST", headers: adminHeaders() });
      const json = await res.json();
      setValidateResult(json);
      load();
    } catch { setValidateResult({ error: "Validation failed." }); }
    finally { setValidating(false); }
  }

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${API_URL}/api/scholarships?active=${activeFilter}`, { headers: adminHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      setScholarships(json.scholarships || []);
    } catch (e) { setError(e.message || "Failed to load."); }
    finally { setLoading(false); }
  }, [activeFilter]);

  useEffect(() => { load(); }, [load]);

  async function toggleActive(s) {
    setTogglingId(s.id);
    try {
      await fetch(`${API_URL}/api/scholarships/${s.id}`, {
        method: "PATCH", headers: adminHeaders(),
        body: JSON.stringify({ is_active: !s.is_active })
      });
      load();
    } catch { setError("Toggle failed."); }
    finally { setTogglingId(null); }
  }

  const filtered = scholarships.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.provider_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ minHeight: "100vh", background: "#f0f4f8", fontFamily: "sans-serif" }}>

      {/* Header */}
      <div style={{ background: "#1f4e79", padding: "14px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>Your Scholarship Finder — Admin</div>
          <div style={{ color: "#aed6f1", fontSize: 11 }}>
            {tab === "scholarships" ? "Scholarship Management" : tab === "students" ? "Student Tracker" : tab === "partnerships" ? "Partnership Orders" : "Platform Metrics"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Btn variant="secondary" onClick={() => window.location.href = "/"}>← Dashboard</Btn>
          {tab === "scholarships" && (
            <>
              <Btn variant="success" onClick={() => setEditing("new")}>+ Add Scholarship</Btn>
              <Btn variant="secondary" onClick={validateUrls} disabled={validating}>
                {validating ? "Checking URLs…" : "🔗 Validate URLs"}
              </Btn>
            </>
          )}
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ background: "#fff", borderBottom: "2px solid #e2e8f0", padding: "0 28px", display: "flex", gap: 0 }}>
        {[
          { key: "scholarships", label: "🎓 Scholarships" },
                    { key: "students",     label: "👤 Students" },
          { key: "partnerships", label: "🤝 Partnerships" },
          { key: "metrics",      label: "📊 Metrics" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: "12px 20px", fontSize: 13, fontWeight: 600, border: "none",
            borderBottom: tab === t.key ? "2px solid #1f4e79" : "2px solid transparent",
            background: "none", color: tab === t.key ? "#1f4e79" : "#6b7a8d",
            cursor: "pointer", marginBottom: -2, fontFamily: "sans-serif"
          }}>{t.label}</button>
        ))}
      </div>

      {/* Validate URL result banner */}
      {validateResult && tab === "scholarships" && (
        <div style={{ background: validateResult.error ? "#fadbd8" : "#d5f5e3", padding: "10px 28px", fontSize: 13, fontFamily: "sans-serif", color: validateResult.error ? "#922b21" : "#1a6b3a", fontWeight: 500 }}>
          {validateResult.error
            ? `⚠ ${validateResult.error}`
            : `✓ Validation complete — ${validateResult.ok} ok, ${validateResult.broken} broken out of ${validateResult.total} URLs checked.`
          }
        </div>
      )}

      {/* Tab content */}
            {tab === "metrics" ? (
        <AdminMetrics />
      ) : tab === "partnerships" ? (
        <PartnershipsTab />
      ) : tab === "students" ? (
        <StudentsTab />
      ) : (
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 16px" }}>

          {error && <div style={ls.errorBanner}>{error}</div>}

          {/* Filters */}
          <div style={{ background: "#fff", borderRadius: 10, padding: "14px 18px", marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", border: "1px solid #e2e8f0" }}>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or provider…"
              style={{ ...ls.input, flex: 1, minWidth: 200, marginBottom: 0 }}
            />
            <div style={{ display: "flex", gap: 6 }}>
              {[["true","Active"],["false","Inactive"],["","All"]].map(([val, label]) => (
                <button key={val} onClick={() => setActiveFilter(val)} style={{
                  fontSize: 12, padding: "6px 14px", borderRadius: 6, border: "1px solid",
                  borderColor: activeFilter === val ? "#1f4e79" : "#d0d8e4",
                  background: activeFilter === val ? "#1f4e79" : "#fff",
                  color: activeFilter === val ? "#fff" : "#4a5568",
                  cursor: "pointer", fontWeight: activeFilter === val ? 600 : 400
                }}>{label}</button>
              ))}
            </div>
            <span style={{ fontSize: 12, color: "#8a95a3" }}>{filtered.length} records</span>
          </div>

          {/* Table */}
          <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f8faff", borderBottom: "2px solid #e2e8f0" }}>
                  {["Name", "Provider", "Amount", "Deadline", "Platform", "Status", "Actions"].map(h => (
                    <th key={h} style={ls.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{ textAlign: "center", padding: "40px", color: "#8a95a3" }}>Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: "center", padding: "40px", color: "#8a95a3" }}>No scholarships found.</td></tr>
                ) : filtered.map(s => {
                  const numMin = parseFloat(s.amount_min) || 0;
                  const numMax = parseFloat(s.amount_max) || 0;
                  const amount = (!numMin && !numMax) ? 'Varies'
                    : (numMin && !numMax) ? `$${numMin.toLocaleString()}`
                    : (!numMin && numMax) ? `$${numMax.toLocaleString()}`
                    : (numMin === numMax) ? `$${numMin.toLocaleString()}`
                    : `$${numMin.toLocaleString()} – $${numMax.toLocaleString()}`;
                  const deadline = s.deadline ? new Date(s.deadline.slice(0,10).replace(/-/g,"/")).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "Rolling";
                  return (
                    <tr key={s.id} style={{ borderBottom: "1px solid #f0f4f8", opacity: s.is_active ? 1 : 0.6 }}>
                      <td style={ls.td}><div style={{ fontWeight: 600, fontSize: 13, color: "#1c2833" }}>{s.name}</div></td>
                      <td style={ls.td}><span style={{ fontSize: 12, color: "#6b7a8d" }}>{s.provider_name}</span></td>
                      <td style={ls.td}><span style={{ fontSize: 13, fontWeight: 600, color: "#1d9e75" }}>{amount}</span></td>
                      <td style={ls.td}><span style={{ fontSize: 12, color: "#6b7a8d" }}>{deadline}</span></td>
                      <td style={ls.td}><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "#ebf5fb", color: "#2e75b6", fontWeight: 600 }}>{s.source_platform}</span></td>
                      <td style={ls.td}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <Badge active={s.is_active} />
                          {s.url_status === "broken" && (
                            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, fontWeight: 600, background: "#fadbd8", color: "#922b21" }}>🔗 Broken URL</span>
                          )}
                        </div>
                      </td>
                      <td style={{ ...ls.td, whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <Btn variant="secondary" onClick={() => setEditing(s)}>Edit</Btn>
                          <Btn
                            variant={s.is_active ? "danger" : "success"}
                            onClick={() => toggleActive(s)}
                            disabled={togglingId === s.id}
                          >
                            {togglingId === s.id ? "…" : s.is_active ? "Deactivate" : "Reactivate"}
                          </Btn>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <ScholarshipForm
          initial={editing === "new" ? null : editing}
          allScholarships={scholarships}
          onSave={() => { setEditing(null); load(); }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────

const ls = {
  label:  { display: "block", fontSize: 12, fontWeight: 600, color: "#2c3e50", marginBottom: 5, fontFamily: "sans-serif" },
  input:  { width: "100%", boxSizing: "border-box", padding: "8px 12px", border: "1.5px solid #d0d8e4", borderRadius: 6, fontSize: 13, color: "#2c3e50", fontFamily: "sans-serif", background: "#fafcff" },
  th:     { padding: "10px 14px", fontSize: 11, fontWeight: 700, color: "#6b7a8d", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.05em" },
  td:     { padding: "12px 14px", fontSize: 13, verticalAlign: "middle" },
  error:  { color: "#c0392b", background: "#fdf0ef", border: "1px solid #f5c6cb", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 16, fontFamily: "sans-serif" },
  errorBanner: { background: "#fadbd8", border: "1px solid #e74c3c", borderRadius: 10, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#922b21" },
  modal:  { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modalBox: { background: "#fff", borderRadius: 12, padding: "28px 32px", width: "100%", maxWidth: 700, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" },
  modalTitle: { fontSize: "1.2rem", fontWeight: 700, color: "#1a3a5c", margin: 0 },
};
